import { useAuth } from "@clerk/nextjs";
import { SIGNUP_COOKIES_NAME } from "helpers/services/signupCookies";
import { useEffect } from "react";
import { getCookie, setCookie } from "react-use-cookie";
import { SignupCookieData } from "../../types/signupAttribution";

const COOKIE_DAYS = 30;

/**
 * Hook to handle first-touch attribution tracking for non-authenticated users
 */
export default function useSignupCookies() {
  const { isSignedIn, isLoaded } = useAuth();

  useEffect(() => {
    // Only capture for non-authenticated users
    if (!isSignedIn && isLoaded) {
      captureSignupAttribution();
    }
  }, [isSignedIn, isLoaded]);
}

/**
 * Checks if a URL is from the Consensus domain
 */
function isConsensusReferrer(referrer: string): boolean {
  if (!referrer) return false;

  try {
    const url = new URL(referrer);
    // Check for consensus.app and any subdomains, as well as localhost for development
    return (
      url.hostname === "consensus.app" ||
      url.hostname.endsWith(".consensus.app") ||
      url.hostname === "localhost" ||
      url.hostname === "127.0.0.1"
    );
  } catch {
    // If URL parsing fails, treat as external
    return false;
  }
}

/**
 * Captures first-touch attribution data
 * This is internal to the hook and only called for non-authenticated users
 */
function captureSignupAttribution(): void {
  // Only capture if cookie doesn't exist (preserve first-touch attribution)
  if (getCookie(SIGNUP_COOKIES_NAME)) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);

  // For internal referrers, we don't want to capture them as the referrer
  // but we still want to capture UTM params if they exist (user might have landed with UTMs)
  const referrer = isConsensusReferrer(document.referrer)
    ? undefined
    : document.referrer;

  const data: SignupCookieData = {
    referrer: referrer || undefined,
    utm_campaign: urlParams.get("utm_campaign") || undefined,
    utm_term: urlParams.get("utm_term") || undefined,
    utm_source: urlParams.get("utm_source") || undefined,
    utm_medium: urlParams.get("utm_medium") || undefined,
    first_touch_url: window.location.href,
    timestamp: Date.now(),
  };

  // Only set cookie if we have meaningful data
  // This includes: external referrer, UTM parameters, or direct visits (no referrer but has UTMs)
  if (referrer || data.utm_source || data.utm_campaign) {
    setCookie(SIGNUP_COOKIES_NAME, JSON.stringify(data), {
      days: COOKIE_DAYS,
    });

    if (process.env.NODE_ENV === "development") {
      console.log("Signup attribution captured:", data);
    }
  } else if (process.env.NODE_ENV === "development") {
    console.log(
      "Skipping attribution capture: internal referrer with no UTM params",
      document.referrer
    );
  }
}
